import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { produce } from 'immer';
import { TvhLanguage } from '../constants/language.constant';
import { NgxsStateFacade } from './ngxs-state-facade';

export interface LangStateModel {
  lang: TvhLanguage;
}

export class ClearLangSelection {
  static readonly type = '[Lang] Clear Lang Selection';
}

export class SetAppLanguage {
  static readonly type = '[Lang] Set App Language';
  constructor(public lang: TvhLanguage) {}
}

@State<LangStateModel>({
  name: 'lang',
  defaults: {
    lang: undefined,
  },
})
@Injectable()
export class LangState extends NgxsStateFacade<LangStateModel> {
  constructor(
    store: Store,
    private readonly translate: TranslateService,
  ) {
    super(store, 'lang');
  }

  @Selector()
  static lang(state: LangStateModel): TvhLanguage {
    return state.lang;
  }

  // --- Public API ---

  get lang(): TvhLanguage {
    return this.store.selectSnapshot(LangState.lang);
  }

  ngxsAfterBootstrap(): void {
    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setDefaultLang('en');
    // the lang to use, if the lang isn't available, it will use the current loader to get them
    if (this.lang) {
      this.translate.use(this.lang);
    }
  }

  clearLangSelection(): void {
    this.store.dispatch(new ClearLangSelection());
  }

  setAppLanguage(lang: TvhLanguage): void {
    this.store.dispatch(new SetAppLanguage(lang));
  }

  // --- Action Handlers ---

  @Action(ClearLangSelection)
  clearLangSelectionHandler(ctx: StateContext<LangStateModel>) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        draft.lang = undefined;
      }),
    );
  }

  @Action(SetAppLanguage)
  setAppLanguageHandler(ctx: StateContext<LangStateModel>, action: SetAppLanguage) {
    this.translate.use(action.lang);
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        draft.lang = action.lang;
      }),
    );
  }
}
